import { call, put, race, take, takeLatest } from 'redux-saga/effects';

import { monolithApiUrl } from '../../config/app';
import { endpoints, queryParams } from '../../constants/routing';
import { IFetchCampaignSummariesResponsePayload } from '../../types/campaigns';
import { IResponse } from '../../types/http';
import { IReduxAction } from '../../types/redux';
import {
  IDownloadReportsCSVRequestPayload,
  IFetchReportByIdRequestPayload,
  IFetchReportsRequestPayload,
  IFetchReportsResponse,
  IPopulateReportsCampaignSummariesRequestPayload,
  ReportTypesEnum,
} from '../../types/reports';
import { IDownloadCSVSuccessPayload } from '../../types/shell';
import { splitPickCampaignIds } from '../../utils/marketplaceSend';
import { getQuery } from '../../utils/request';
import { buildEndpointRoute } from '../../utils/routing';
import * as CampaignActions from '../actions/campaigns';
import * as ReportsActions from '../actions/reports';
import { AuthRequest } from './helpers';

export function* deliveryReportsWorkerSaga(action: IReduxAction<IFetchReportsRequestPayload>) {
  try {
    const { body, type = ReportTypesEnum.Regular } = action.payload || {};

    const endpoint = `${monolithApiUrl}${endpoints.reports}`;

    const response: IResponse<IFetchReportsResponse> = yield call(AuthRequest, {
      endpoint,
      method: 'POST',
      body: JSON.stringify(body),
      queryParams: getQuery(type, queryParams.type),
    });
    if (!response.ok) {
      yield put(ReportsActions.fetchReportsFailure(response.body));
    } else {
      yield put(ReportsActions.fetchReportsSuccess({ type, ...response.body }));
    }
  } catch (error) {
    yield put(ReportsActions.fetchReportsFailure(error));
  }
}

export function* deliveryReportByIdWorkerSaga(
  action: IReduxAction<IFetchReportByIdRequestPayload>,
): Generator<any, any, any> {
  const { reportId, type, resolve, reject } = action.payload || {};

  try {
    if (!reportId) {
      throw new Error('Report ID is required');
    }

    const endpoint = `${monolithApiUrl}${buildEndpointRoute(endpoints.report, { id: reportId })}`;

    const response = yield call<any>(AuthRequest, {
      endpoint,
      method: 'GET',
      queryParams: getQuery(type, queryParams.type),
    });

    if (!response.ok) {
      if (reject) {
        reject(response.body);
      }
      yield put(ReportsActions.fetchReportByIdFailure(response.body));
    } else {
      if (resolve) {
        resolve(response.body);
      }
      yield put(ReportsActions.fetchReportByIdSuccess({ ...response.body, type }));
    }
  } catch (error) {
    if (reject) {
      reject(error);
    }
    yield put(ReportsActions.fetchReportByIdFailure(error));
  }
}

export function* populateReportsCampaignSummariesWorkerSaga(
  action: IReduxAction<IPopulateReportsCampaignSummariesRequestPayload>,
): Generator<any, any, any> {
  const { type, reportId, campaign_ids } = action.payload!;

  try {
    const { campaignIds } = splitPickCampaignIds(campaign_ids);

    if (!campaignIds.length) {
      yield put(ReportsActions.populateReportCampaignSummariesSuccess({ campaign_summaries: [], type, reportId }));
      return;
    }

    yield put(CampaignActions.fetchCampaignSummariesRequest({ campaign_ids: campaignIds }));

    const [successAction, failureAction] = yield race([
      take(CampaignActions.FETCH_CAMPAIGN_SUMMARIES_SUCCESS),
      take(CampaignActions.FETCH_CAMPAIGN_SUMMARIES_FAILURE),
    ]);

    if (successAction) {
      yield put(
        ReportsActions.populateReportCampaignSummariesSuccess({
          ...((successAction as IReduxAction<IFetchCampaignSummariesResponsePayload>)
            .payload as IFetchCampaignSummariesResponsePayload),
          type,
          reportId,
        }),
      );
    } else {
      yield put(
        ReportsActions.populateReportCampaignSummariesFailure({
          type,
          reportId,
          error: failureAction?.payload ?? new Error('Unexpected response from GetCampaignSummaries'),
        }),
      );
    }
  } catch (error) {
    yield put(ReportsActions.populateReportCampaignSummariesFailure({ type, reportId, error }));
  }
}

export function* downloadReportsCSVWorkerSaga(action: IReduxAction<IDownloadReportsCSVRequestPayload>) {
  const {
    type = ReportTypesEnum.Regular,
    department_ids,
    date_range,
    search_query,
    source_type,
    status,
    resolve,
    reject,
    inventory_breakdown,
    one_link_id,
    item_id,
    campaign_id,
    fulfillment_center_ids,
  } = action.payload || {};

  try {
    const endpoint = `${monolithApiUrl}${endpoints.downloadReportsCSV}`;
    const body = JSON.stringify({
      ...(type ? getQuery(type, queryParams.engagementType) : {}),
      ...(department_ids && department_ids.length ? { department_ids } : {}),
      ...(search_query ? { search_query } : {}),
      ...(source_type ? { source_type } : {}),
      ...(item_id ? { item_id } : {}),
      ...(campaign_id ? { campaign_id } : {}),
      ...(status && { status }),
      ...(fulfillment_center_ids && fulfillment_center_ids.length ? { fulfillment_center_ids } : {}),
      inventory_breakdown,
      date_range,
      one_link_id,
    });

    const response: IResponse<IDownloadCSVSuccessPayload> = yield call(AuthRequest, {
      endpoint,
      method: 'POST',
      body,
    });

    if (!response.ok) {
      if (reject) {
        yield call(reject, response.body);
      }
      yield put(ReportsActions.downloadReportsCSVFailure(response.body));
    } else {
      yield put(ReportsActions.downloadReportsCSVSuccess(response.body));
      if (resolve) {
        yield call(resolve, response.body);
      }
    }
  } catch (e) {
    if (reject) {
      yield call(reject, e);
    }
    yield put(ReportsActions.downloadReportsCSVFailure(e));
  }
}

const sagas = {
  *watchDeliveryReportsRequest() {
    yield takeLatest(ReportsActions.FETCH_REPORTS_REQUEST, deliveryReportsWorkerSaga);
  },
  *watchDeliveryReportByIdRequest() {
    yield takeLatest(ReportsActions.FETCH_REPORT_BY_ID_REQUEST, deliveryReportByIdWorkerSaga);
  },
  *watchFetchReportsCampaignSummariesRequest() {
    yield takeLatest(
      ReportsActions.POPULATE_REPORT_CAMPAIGN_SUMMARIES_REQUEST,
      populateReportsCampaignSummariesWorkerSaga,
    );
  },
  *watchDownloadReportsCSVRequest() {
    yield takeLatest(ReportsActions.DOWNLOAD_REPORTS_CSV_REQUEST, downloadReportsCSVWorkerSaga);
  },
};

export default sagas;
